import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const photoCarouselIndicesByKey = {};

function normalizeSrc(path) {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${API_BASE_URL}${path}`;
}

function clampIndex(rawIndex, length) {
    if (!length) return 0;
    return Math.min(Math.max(0, rawIndex), length - 1);
}

export default function PhotoCarousel({ title, photos, stateKey = "default" }) {
    const items = useMemo(() => (Array.isArray(photos) ? photos.filter(Boolean) : []), [photos]);

    const readSavedIndex = useCallback(
        () => clampIndex(photoCarouselIndicesByKey[stateKey] ?? 0, items.length),
        [items.length, stateKey],
    );

    const [index, setIndex] = useState(readSavedIndex);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    useEffect(() => {
        const safeIndex = readSavedIndex();
        photoCarouselIndicesByKey[stateKey] = safeIndex;
        setIndex(safeIndex);
    }, [readSavedIndex, stateKey]);

    useEffect(() => {
        setIndex((prev) => {
            const safeIndex = clampIndex(prev, items.length);
            photoCarouselIndicesByKey[stateKey] = safeIndex;
            return safeIndex;
        });
    }, [items, stateKey]);

    useEffect(() => {
        if (!lightboxOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setLightboxOpen(false);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [lightboxOpen]);

    if (!items.length) return null;

    const safeIndex = clampIndex(index, items.length);
    const src = normalizeSrc(items[safeIndex]);
    const canSlide = items.length > 1;

    const goPrev = () => {
        setIndex((prev) => {
            const next = (clampIndex(prev, items.length) - 1 + items.length) % items.length;
            photoCarouselIndicesByKey[stateKey] = next;
            return next;
        });
    };

    const goNext = () => {
        setIndex((prev) => {
            const next = (clampIndex(prev, items.length) + 1) % items.length;
            photoCarouselIndicesByKey[stateKey] = next;
            return next;
        });
    };

    return (
        <section className="photo-carousel">
            <div className="photo-carousel__frame">
                <div className="photo-carousel__media">
                    <button
                        type="button"
                        className="photo-carousel__image-btn"
                        onClick={() => setLightboxOpen(true)}
                        aria-label="Открыть фото в полном размере"
                    >
                        <img src={src} alt={title || "Фото"} width={800} height={600} decoding="async" />
                    </button>
                    {canSlide && (
                        <>
                            <div className="photo-carousel__counter" aria-live="polite">
                                {safeIndex + 1} / {items.length}
                            </div>
                            <button
                                type="button"
                                className="photo-carousel__nav photo-carousel__nav--prev"
                                onClick={goPrev}
                                aria-label="Предыдущее фото"
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                className="photo-carousel__nav photo-carousel__nav--next"
                                onClick={goNext}
                                aria-label="Следующее фото"
                            >
                                ›
                            </button>
                        </>
                    )}
                </div>
            </div>
            {lightboxOpen && createPortal(
                <div className="comment-photo-lightbox" onClick={() => setLightboxOpen(false)}>
                    <button
                        type="button"
                        className="comment-photo-lightbox__close"
                        onClick={(event) => {
                            event.stopPropagation();
                            setLightboxOpen(false);
                        }}
                        aria-label="Закрыть фото"
                    >
                        ×
                    </button>
                    <img
                        src={src}
                        alt={title || "Фото"}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>,
                document.body,
            )}
        </section>
    );
}
